import { Menu, Transition } from '@headlessui/react';
import { CheckedIcon, ChecklistIcon, GoIcon } from 'components/icons';
import { prebuiltSegments } from 'components/segments/prebuiltSegments';
import { compact, find } from 'lodash';
import { Link } from 'react-router-dom';
import { useAppSelector } from 'store';
import {
  useGetCustomerSegmentsQuery,
  useGetStoreInfoQuery,
  useGetUserInfoQuery,
} from 'store/services/segmentsApi';
import { userDetailsState } from 'store/userDetailsSlice';
import {
  OnboardingStatus,
  useUserOnboardingStatus,
} from 'utils/useUserOnboardingStatus';

export default function OnboardingChecklist() {
  const { currentStore } = useAppSelector(userDetailsState);

  const { data: userInfo } = useGetUserInfoQuery();

  const userOnboarding = useUserOnboardingStatus();

  const { data: storeInfo, isFetching } = useGetStoreInfoQuery(
    currentStore.serializedStoreName,
    {
      skip: !!!currentStore.serializedStoreName,
    }
  );

  const { data: segments } = useGetCustomerSegmentsQuery(
    { serializedStoreName: currentStore.serializedStoreName },
    {
      skip:
        !!!currentStore.serializedStoreName ||
        userOnboarding.status === OnboardingStatus.IN_PROGRESS,
    }
  );

  const segmentsAutoSynced = compact(
    segments?.filter((segment) =>
      segment.integrations.find((item) => item.auto_synced)
    )
  );

  const status = {
    hasOnboarded: userInfo?.has_objectives,
    hasVerifiedEmail: userInfo?.confirmed,
    hasEspConnected: Boolean(storeInfo?.integrations.length),
    hasCreatedSegment: Boolean(
      find(segments, (_, key) => !!!(key in prebuiltSegments))
    ),
    hasAutoSyncSegment: segmentsAutoSynced.length > 0,
    hasInvitedMembers: Boolean(storeInfo && storeInfo.users.length > 1),
  };

  const pendingOnboardItems =
    userInfo &&
    storeInfo &&
    segments &&
    Object.values(status).filter((item) => !item).length;

  if (
    !!!pendingOnboardItems ||
    userOnboarding.status === OnboardingStatus.IN_PROGRESS
  ) {
    return null;
  }

  return (
    <div className="relative mx-4 mt-2.5 ">
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button className="w-full focus:outline-none">
              <div className="flex items-center justify-start h-10 pr-6 space-x-4 font-normal leading-4 text-white bg-white text-segments-13px rounded-xl bg-opacity-30">
                <div className="flex items-center justify-center h-full px-3 bg-segments-lighter-gray rounded-l-xl ">
                  <ChecklistIcon />
                </div>

                <div className="flex items-center justify-between w-full">
                  <span className="">Onboarding checklist</span>
                  {pendingOnboardItems && !isFetching && (
                    <span className="relative flex w-5 h-5">
                      {/* <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full opacity-75 animate-ping"></span> */}
                      <span className="relative inline-flex items-center justify-center w-5 h-5 bg-red-500 rounded-full">
                        {pendingOnboardItems}
                      </span>
                    </span>
                  )}
                  <svg
                    className="w-4 h-4 text-white stroke-current"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </Menu.Button>

            <Transition
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 "
              enterTo="opacity-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 "
              leaveTo="opacity-0"
            >
              <Menu.Items
                static
                className="absolute left-0 w-full mt-2 origin-bottom-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none"
              >
                <OnboardingChecklistItem
                  description="Set up Segments"
                  linkTo={{
                    pathname: '/onboarding',
                    search: currentStore.serializedStoreName,
                  }}
                  completed={status.hasOnboarded}
                  disabled={Boolean(status.hasOnboarded)}
                />
                <OnboardingChecklistItem
                  description="Verify account / email"
                  completed={status.hasVerifiedEmail}
                  linkTo={{
                    pathname: '/account',
                    search: currentStore.serializedStoreName,
                  }}
                />
                <OnboardingChecklistItem
                  description="Connect a provider"
                  completed={status.hasEspConnected}
                  linkTo={{
                    pathname: '/integrations',
                    search: currentStore.serializedStoreName,
                  }}
                />
                <OnboardingChecklistItem
                  description="Invite team members"
                  linkTo={{
                    pathname: '/account/stores',
                    search: currentStore.serializedStoreName,
                  }}
                  completed={status.hasInvitedMembers}
                />
                <OnboardingChecklistItem
                  description="Create a segment"
                  completed={status.hasCreatedSegment}
                  linkTo={{
                    pathname: '/customers/create',
                    search: currentStore.serializedStoreName,
                  }}
                />

                <OnboardingChecklistItem
                  description="Auto-sync segments"
                  completed={status.hasAutoSyncSegment}
                  linkTo={{
                    pathname: '/customers/segments',
                    search: currentStore.serializedStoreName,
                  }}
                />
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
}

function OnboardingChecklistItem({
  linkTo,
  description,
  completed,
  disabled,
}: {
  linkTo: string | Object;
  description: string;
  completed?: boolean | null;
  disabled?: boolean;
}) {
  return (
    <Menu.Item
      as={Link}
      disabled={disabled}
      to={linkTo}
      className={`flex justify-between w-full px-4 py-3 text-sm leading-5 text-left text-gray-700 hover:bg-segments-gray-F1 ${
        disabled && 'cursor-not-allowed'
      }`}
    >
      <div className="flex items-center space-x-2 ">
        <div className="w-5 text-segments-accent-green-saved">
          {completed && <CheckedIcon className="w-5 h-5" />}
        </div>
        <p className={completed ? 'text-opacity-50 text-black' : ''}>
          {description}
        </p>
      </div>
      <div>{!!!completed && <GoIcon />}</div>
    </Menu.Item>
  );
}
